import { TableBody, TableCell, TableRow } from "@windmill/react-ui";
import { FiDownload, FiEye, FiCheck, FiX } from "react-icons/fi";
import Tooltip from "components/tooltip/Tooltip";
import useToggleDrawer from "hooks/useToggleDrawer";
import CareerServices from "services/CareerServices";
import React, { useState } from "react";
import ViewModal from "components/modal/ViewCareer";
import ShowContact from "components/table/showContact";
import { notifyError, notifySuccess } from "utils/toast";

// Reject Modal Component
const RejectModal = ({ open, onClose, onSubmit }) => {
  const [reason, setReason] = useState("");

  const handleSubmit = () => {
    if (!reason.trim()) {
      notifyError("Please provide a reason");
      return;
    }
    onSubmit(reason);
    setReason("");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-md w-[900px] max-w-full">
        <h2 className="text-lg font-semibold mb-4">Reason for Rejection</h2>
        <textarea
          className="w-full h-40 p-3 border rounded-md dark:bg-gray-700 dark:border-gray-600 resize-none"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for rejection..."
        />
        <div className="flex justify-end mt-5 gap-3">
          <button
            className="px-5 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
            onClick={handleSubmit}
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
};

// View Reason Modal Component
const ReasonModal = ({ open, reason, onClose }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-md w-[600px] max-w-full">
        <h2 className="text-lg font-semibold mb-4">Rejection Reason</h2>
        <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">{reason}</p>
        <div className="flex justify-end mt-5">
          <button
            className="px-5 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const CareerTable = ({ career, perpage, currentPage, fetchCareers }) => {
  const { serviceId, handleView, title } = useToggleDrawer();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  // Download Resume
  const handleDownloadResume = async (resumeId) => {
    try {
      const baseURL = process.env.REACT_APP_API_IMAGE_URL?.replace("", "");
      const fileUrl = `${baseURL}/${resumeId}`;
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resumeId.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading resume:", error);
      notifyError("Failed to download resume");
    }
  };

  // View Resume
  const handleViewResume = (resumeId, resumeUrl) => {
    if (resumeUrl) {
      const baseURL = process.env.REACT_APP_API_IMAGE_URL?.replace("", "");
      const fileUrl = `${baseURL}/${resumeUrl}`;
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Toggle Contact Status
// Toggle Contact Status
const handleContact = async (id, currentStatus) => {
  try {
    // Toggle status: if currentStatus is 1 → set 0, else 1
    const newStatus = currentStatus ? 0 : 1;

    // Call the service
    const response = await CareerServices.updateStatus(id, { is_connected: newStatus });

    // Handle response correctly
    if (response?.status || response?.success) { // Use 'status' or 'success'
      notifySuccess(response.message || "Status Updated Successfully");

      // Refresh the data if fetchCareers exists
      if (typeof fetchCareers === "function") {
        await fetchCareers();
      }
    } else {
      notifyError(response?.message || "Failed to update status");
    }
  } catch (error) {
    notifyError("An error occurred while updating status");
    console.error(error);
  }
};


// Update Application Status (Shortlist / Reject)
const handleStatusUpdate = async (id, newStatus, rejectReason = "") => {
  try {
    const payload = { status: newStatus };
    if (newStatus === 2) payload.reject_reason = rejectReason;

    const response = await CareerServices.updateCareerStatus(id, payload);

    if (response.success) { // 🔹 use response.success, not response.status
      const statusMessage = newStatus === 1 ? "shortlisted" : "rejected";
      notifySuccess(response.message || `Application ${statusMessage} successfully`);

      if (fetchCareers) await fetchCareers(); // 🔹 refresh the table
    } else {
      notifyError(response.message || "Failed to update application status");
    }
  } catch (error) {
    notifyError("An error occurred while updating status");
    console.error(error);
  }
};

// Submit Reject Reason
const submitRejectReason = async (reason) => {
  if (rejectingId) {
    await handleStatusUpdate(rejectingId, 2, reason); // 🔹 await refresh
    setRejectingId(null);
  }
};

  const openRejectModal = (id) => {
    setRejectingId(id);
    setRejectModalOpen(true);
  };
  const openReasonModal = (reason) => {
    setSelectedReason(reason || "No reason provided");
    setReasonModalOpen(true);
  };

  return (
    <>
      <ViewModal id={serviceId} title={title} data={career} />
      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onSubmit={submitRejectReason}
      />
      <ReasonModal
        open={reasonModalOpen}
        reason={selectedReason}
        onClose={() => setReasonModalOpen(false)}
      />
      <TableBody>
        {career?.slice(0, perpage).map((data, index) => (
          <TableRow key={data.id}>
            <TableCell className="text-sm text-center">
              {(currentPage - 1) * perpage + index + 1}
            </TableCell>
            <TableCell className="text-sm text-center">{data.name}</TableCell>
            <TableCell className="text-sm text-center">{data.job_roles}</TableCell>
            <TableCell className="text-sm text-center">
              {new Date(data.added_date).toLocaleString()}
            </TableCell>
            <TableCell className="text-sm text-center">{data.contact_no}</TableCell>

            <TableCell className="text-center">
              <div className="flex items-center justify-center space-x-2">
                {data.resume && (
                  <button
                    onClick={() => handleDownloadResume(data.resume)}
                    className="text-blue-500 hover:underline flex items-center cursor-pointer bg-transparent border-none p-0"
                  >
                    <FiDownload className="mr-2" />
                  </button>
                )}
                <button
                  onClick={() => handleViewResume(data.id, data.resume)}
                  className="p-2 cursor-pointer text-gray-400 hover:text-blue-600 focus:outline-none flex items-center"
                >
                  <Tooltip
                    id={`view-resume-${data.id}`}
                    Icon={FiEye}
                    title="View Resume"
                    bgColor="#34D399"
                    aria-label="View Resume"
                  />
                </button>
              </div>
            </TableCell>

            <TableCell className="text-center">
              <div className="flex flex-col items-center justify-center">
                <ShowContact
                  id={data.id}
                  active={data.is_connected === 1}
                  title_active="Contact"
                  handleContact={(id, status) => handleContact(id, status)}
                />
                <span className="text-sm">
                  {data.is_connected === 1 ? "Connected" : "Unconnected"}
                </span>
              </div>
            </TableCell>

            <TableCell className="text-center">
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handleView(data.id, "Career Details")}
                  className="p-2 cursor-pointer text-gray-400 hover:text-green-600 focus:outline-none"
                >
                  <Tooltip
                    id={data.id}
                    Icon={FiEye}
                    title="View Details"
                    bgColor="#34D399"
                  />
                </button>

                {data.status === 0 && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(data.id, 1)}
                      className="p-2 cursor-pointer text-gray-400 hover:text-green-600 focus:outline-none"
                    >
                      <Tooltip
                        id={`shortlist-${data.id}`}
                        Icon={FiCheck}
                        title="Shortlist Application"
                        bgColor="#10B981"
                      />
                    </button>
                    <button
                      onClick={() => openRejectModal(data.id)}
                      className="p-2 cursor-pointer text-gray-400 hover:text-red-600 focus:outline-none"
                    >
                      <Tooltip
                        id={`reject-${data.id}`}
                        Icon={FiX}
                        title="Reject Application"
                        bgColor="#EF4444"
                      />
                    </button>
                  </>
                )}

                {data.status === 1 && (
                  <span className="text-green-600 font-semibold">Shortlisted</span>
                )}
                {data.status === 2 && (
                  <span className="text-red-600 font-semibold">Rejected</span>
                )}
              </div>
            </TableCell>

            {/* Separate Reason Column */}
            <TableCell className="text-center">
              {data.reject_reason && (
                <button
                  onClick={() => openReasonModal(data.reject_reason)}
                  className="text-blue-500 hover:underline"
                >
                  View Reason
                </button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
};

export default CareerTable;
